import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ApprovalWorkflow = ({ workflow, onApprove, onReject, onRequestChanges, onEscalate }) => {
  const [comment, setComment] = useState('');
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [actionType, setActionType] = useState('');

  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'text-yellow-600 bg-yellow-50 border-yellow-200',
      'Approved': 'text-green-600 bg-green-50 border-green-200',
      'Rejected': 'text-red-600 bg-red-50 border-red-200',
      'Changes Requested': 'text-orange-600 bg-orange-50 border-orange-200',
      'Escalated': 'text-purple-600 bg-purple-50 border-purple-200'
    };
    return colors?.[status] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Pending': 'Clock',
      'Approved': 'CheckCircle',
      'Rejected': 'XCircle',
      'Changes Requested': 'AlertCircle',
      'Escalated': 'ArrowUp'
    };
    return icons?.[status] || 'Clock';
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleActionClick = (action) => {
    setActionType(action);
    setShowCommentInput(true);
  };

  const handleSubmitAction = () => {
    if (!comment?.trim()) return;

    switch (actionType) {
      case 'approve':
        onApprove && onApprove(comment);
        break;
      case 'reject':
        onReject && onReject(comment);
        break;
      case 'changes':
        onRequestChanges && onRequestChanges(comment);
        break;
      case 'escalate':
        onEscalate && onEscalate(comment);
        break;
    }

    setComment('');
    setShowCommentInput(false);
    setActionType('');
  };

  const handleCancelAction = () => {
    setComment('');
    setShowCommentInput(false);
    setActionType('');
  };

  return (
    <div className="bg-card border border-border rounded-lg h-full">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Approval Workflow</h2>
          <div className={`px-2 py-1 rounded-md text-xs font-medium border ${getStatusColor(workflow?.currentStatus)}`}>
            <Icon 
              name={getStatusIcon(workflow?.currentStatus)} 
              size={12} 
              className="inline mr-1" 
            />
            {workflow?.currentStatus}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Priority:</span>
            <span className={`font-medium ${workflow?.priority === 'High' ? 'text-red-600' : 
                                          workflow?.priority === 'Medium' ? 'text-yellow-600' : 'text-green-600'}`}>
              {workflow?.priority}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Assigned to:</span>
            <span className="font-medium text-foreground">{workflow?.assignedTo}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Due date:</span>
            <span className="font-medium text-foreground">{formatDate(workflow?.dueDate)}</span>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-6">
        {/* Action Buttons */}
        {!showCommentInput && workflow?.currentStatus === 'Pending' && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-foreground">Review Actions</h3>
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="success"
                size="sm"
                iconName="Check"
                iconPosition="left"
                fullWidth
                onClick={() => handleActionClick('approve')}
              >
                Approve
              </Button>
              <Button
                variant="destructive"
                size="sm"
                iconName="X"
                iconPosition="left"
                fullWidth
                onClick={() => handleActionClick('reject')}
              >
                Reject
              </Button>
              <Button
                variant="warning"
                size="sm"
                iconName="Edit"
                iconPosition="left"
                fullWidth
                onClick={() => handleActionClick('changes')}
              >
                Request Changes
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="ArrowUp"
                iconPosition="left"
                fullWidth
                onClick={() => handleActionClick('escalate')}
              >
                Escalate
              </Button>
            </div>
          </div>
        )}

        {/* Comment Input */}
        {showCommentInput && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-foreground">
              Add Comment for {actionType?.charAt(0)?.toUpperCase() + actionType?.slice(1)}
            </h3>
            <Input
              type="text"
              placeholder="Enter your comment (required for audit trail)"
              value={comment}
              onChange={(e) => setComment(e?.target?.value)}
              required
            />
            <div className="flex space-x-2">
              <Button
                variant="default"
                size="sm"
                onClick={handleSubmitAction}
                disabled={!comment?.trim()}
              >
                Submit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelAction}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Workflow History */}
        <div>
          <h3 className="text-sm font-medium text-foreground mb-3">Review History</h3>
          <div className="space-y-3 max-h-[300px] overflow-y-auto">
            {workflow?.history?.map((entry, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-muted/30 rounded-md">
                <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-medium text-white">
                    {entry?.reviewer?.charAt(0)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-sm font-medium text-foreground">{entry?.reviewer}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(entry?.action)}`}>
                      {entry?.action}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-1">{entry?.comment}</p>
                  <div className="text-xs text-muted-foreground">
                    {formatDate(entry?.timestamp)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Version History */}
        <div>
          <h3 className="text-sm font-medium text-foreground mb-3">Version History</h3>
          <div className="space-y-2">
            {workflow?.versions?.map((version, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded-md">
                <div className="flex items-center space-x-2">
                  <Icon name="FileText" size={14} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">Version {version?.number}</span>
                  {version?.current && (
                    <span className="px-2 py-0.5 bg-primary text-primary-foreground text-xs rounded">
                      Current
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatDate(version?.timestamp)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Collaboration */}
        <div>
          <h3 className="text-sm font-medium text-foreground mb-3">Active Reviewers</h3>
          <div className="flex items-center space-x-2">
            {workflow?.activeReviewers?.map((reviewer, index) => (
              <div key={index} className="flex items-center space-x-1">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-xs font-medium text-white">
                    {reviewer?.name?.charAt(0)}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">{reviewer?.name}</span>
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalWorkflow;